import { useEffect, useState, useRef } from "react";
import { useLocation } from "wouter";
import { ShieldCheck, Upload, CheckCircle2, Clock, AlertCircle, Loader2, IdCard, Award, Plus, Trash2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

type Doc = "id_image" | "selfie" | "address_proof";
const DOC_LABELS: Record<Doc, string> = {
  id_image: "Government ID",
  selfie: "Selfie holding your ID",
  address_proof: "Proof of address",
};
const ID_TYPES = [
  ["national_id", "National ID (PhilSys)"],
  ["passport", "Passport"],
  ["drivers_license", "Driver's License"],
  ["umid", "UMID"],
  ["sss", "SSS ID"],
];

export default function WorkerVerification() {
  const { user, loading, refresh } = useAuth();
  const [, navigate] = useLocation();
  const [state, setState] = useState<any>(null);
  const [idType, setIdType] = useState(""); // QA #34: no preselected ID type
  const [idNumber, setIdNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [uploading, setUploading] = useState<Doc | null>(null);
  const fileRefs = { id_image: useRef<HTMLInputElement>(null), selfie: useRef<HTMLInputElement>(null), address_proof: useRef<HTMLInputElement>(null) };

  async function load() {
    try { setState(await api.workerKyc()); } catch { setState(null); }
  }
  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);
  useEffect(() => { if (user) load(); }, [user]);

  if (loading || !user) return <div className="container-x py-20 text-center text-mut">Loading…</div>;

  const isWorker = (user.capabilities || []).some((c) => c === "find_work" || c === "offer_service");
  const status = state?.verification?.status || "none";
  const docs = state?.verification?.docs || { id_image: false, selfie: false, address_proof: false };
  const allDocs = docs.id_image && docs.selfie && docs.address_proof;

  async function start() {
    setErr(null); setBusy(true);
    try { await api.startWorkerKyc(idType, idNumber.trim()); await load(); }
    catch (e: any) { setErr(e?.message || "Could not start verification."); }
    finally { setBusy(false); }
  }
  async function pick(doc: Doc) { fileRefs[doc].current?.click(); }
  async function onFile(doc: Doc, file?: File | null) {
    if (!file) return;
    setErr(null); setUploading(doc);
    try { await api.uploadKycDoc(doc, file); await load(); }
    catch (e: any) { setErr(e?.message || "Upload failed."); }
    finally { setUploading(null); }
  }
  async function submit() {
    setErr(null); setBusy(true);
    try { await api.submitWorkerKyc(); await load(); await refresh(); }
    catch (e: any) { setErr(e?.message || "Could not submit."); }
    finally { setBusy(false); }
  }

  return (
    <div className="container-x py-6 max-w-2xl">
      <h1 className="text-2xl font-extrabold text-navy mb-1 flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-brand-red" />Worker verification</h1>
      <p className="text-mut text-sm mb-5">Verify your identity to make your worker profile visible to clients and be hireable. Clients can only find and hire verified workers.</p>

      {!isWorker && (
        <div className="card p-5 text-sm text-navy-700">
          This is for workers. Enable a worker activity (Find Work / Offer a Service) on your account to get verified.
        </div>
      )}

      {isWorker && state && (
        <>
          {status === "approved" || user.workerVerified ? (
            <div className="card p-6 text-center">
              <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
              <h2 className="text-lg font-bold text-navy mt-3">You're a verified worker</h2>
              <p className="text-sm text-mut mt-1">Your profile is live and clients can hire you.</p>
            </div>
          ) : status === "pending" ? (
            <div className="card p-6 text-center">
              <Clock className="w-12 h-12 text-amber-500 mx-auto" />
              <h2 className="text-lg font-bold text-navy mt-3">Under review</h2>
              <p className="text-sm text-mut mt-1">Our team is reviewing your documents. You'll be notified once it's done.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {status === "rejected" && (
                <div className="card p-4 border-l-4 border-brand-red bg-red-50">
                  <div className="font-semibold text-brand-red flex items-center gap-1"><AlertCircle className="w-4 h-4" />Your previous submission needs changes</div>
                  <p className="text-sm text-navy-700 mt-1">{state.verification.decisionNote}</p>
                  <p className="text-xs text-mut mt-1">Update your details and documents below, then resubmit.</p>
                </div>
              )}

              {/* Step 1 — ID details */}
              <div className="card p-5">
                <h3 className="font-bold text-navy mb-3 flex items-center gap-2"><IdCard className="w-4 h-4" />1. Your government ID</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="label">ID type</label>
                    <select className="field" value={idType} onChange={(e) => setIdType(e.target.value)}>
                      <option value="">Select ID Type…</option>
                      {ID_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div><label className="label">ID number</label><input className="field" value={idNumber} onChange={(e) => setIdNumber(e.target.value)} placeholder="As printed on your ID" /></div>
                </div>
                <button onClick={start} disabled={busy || !idType || idNumber.trim().length < 3} className="btn-outline mt-3 disabled:opacity-60">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  {state.verification.idType ? "Update ID details" : "Save ID details"}
                </button>
                {state.verification.idNumber && <p className="text-xs text-brand-green mt-2">Saved: {state.verification.idType} · {state.verification.idNumber}</p>}
              </div>

              {/* Step 2 — documents */}
              <div className="card p-5">
                <h3 className="font-bold text-navy mb-3 flex items-center gap-2"><Upload className="w-4 h-4" />2. Upload documents</h3>
                {!state.verification.idNumber && <p className="text-sm text-mut mb-3">Save your ID details first to enable uploads.</p>}
                <div className="space-y-2">
                  {(Object.keys(DOC_LABELS) as Doc[]).map((doc) => (
                    <div key={doc} className="flex items-center gap-3 p-3 rounded-lg border border-line">
                      <div className={`w-8 h-8 rounded-full grid place-items-center ${docs[doc] ? "bg-emerald-100 text-emerald-700" : "bg-navy-50 text-navy-400"}`}>
                        {docs[doc] ? <CheckCircle2 className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-navy">{DOC_LABELS[doc]}</div>
                        <div className="text-xs text-mut">{docs[doc] ? "Uploaded" : "JPEG, PNG, or PDF · up to 25 MB"}</div>
                      </div>
                      <input ref={fileRefs[doc]} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => onFile(doc, e.target.files?.[0])} />
                      <button onClick={() => pick(doc)} disabled={!state.verification.idNumber || uploading === doc} className="btn-outline h-9 px-3 text-xs disabled:opacity-50">
                        {uploading === doc ? <Loader2 className="w-4 h-4 animate-spin" /> : docs[doc] ? "Replace" : "Upload"}
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {err && <p className="text-sm text-brand-red flex items-center gap-1"><AlertCircle className="w-4 h-4" />{err}</p>}

              <button onClick={submit} disabled={busy || !allDocs} className="btn-red w-full disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                Submit for review
              </button>
              {!allDocs && <p className="text-xs text-mut text-center">Save your ID details and upload all three documents to submit.</p>}
            </div>
          )}

          <CredentialsSection />
        </>
      )}
    </div>
  );
}

function CredentialsSection() {
  const [creds, setCreds] = useState<any[] | null>(null);
  const [type, setType] = useState("");
  const [number, setNumber] = useState("");
  const [issuer, setIssuer] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const pendingUpload = useRef<string | null>(null);

  async function load() { try { setCreds(await api.myCredentials()); } catch { setCreds([]); } }
  useEffect(() => { load(); }, []);

  async function add() {
    setErr(null); setBusy(true);
    try {
      await api.addCredential({ credentialType: type.trim(), credentialNumber: number.trim() || undefined, issuer: issuer.trim() || undefined });
      setType(""); setNumber(""); setIssuer(""); await load();
    } catch (e: any) { setErr(e?.message || "Could not add credential."); }
    finally { setBusy(false); }
  }
  function pickDoc(id: string) { pendingUpload.current = id; fileRef.current?.click(); }
  async function onFile(file?: File | null) {
    const id = pendingUpload.current;
    if (!file || !id) return;
    setUploadingId(id);
    try { await api.uploadCredentialDoc(id, file); await load(); }
    catch (e: any) { setErr(e?.message || "Upload failed."); }
    finally { setUploadingId(null); pendingUpload.current = null; }
  }
  async function remove(id: string) {
    try { await api.deleteCredential(id); await load(); } catch (e: any) { setErr(e?.message || "Could not remove."); }
  }

  const badge: Record<string, string> = { approved: "bg-emerald-100 text-emerald-700", pending: "bg-amber-100 text-amber-700", rejected: "bg-red-100 text-brand-red" };

  return (
    <div className="card p-5 mt-4">
      <h3 className="font-bold text-navy mb-1 flex items-center gap-2"><Award className="w-4 h-4" />Licences & certifications</h3>
      <p className="text-sm text-mut mb-3">Add credentials (e.g. TESDA NC II, PRC licence). Verified credentials strengthen your profile.</p>
      <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />

      {creds && creds.length > 0 && (
        <div className="space-y-2 mb-4">
          {creds.map((c) => (
            <div key={c.id} className="flex items-center gap-3 p-3 rounded-lg border border-line">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-navy">{c.credentialType} {c.credentialNumber && <span className="text-mut font-normal">· {c.credentialNumber}</span>}</div>
                <div className="text-xs text-mut">{c.issuer || "No issuer"}{c.hasDocument ? " · document attached" : " · no document"}</div>
                {c.status === "rejected" && c.decisionNote && <div className="text-xs text-brand-red mt-0.5">{c.decisionNote}</div>}
              </div>
              <span className={`chip capitalize ${badge[c.status] || "bg-navy-50 text-navy-600"}`}>{c.status}</span>
              {c.status !== "approved" && (
                <>
                  <button onClick={() => pickDoc(c.id)} disabled={uploadingId === c.id} className="btn-outline h-8 px-3 text-xs disabled:opacity-50">
                    {uploadingId === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}{c.hasDocument ? "Replace" : "Doc"}
                  </button>
                  <button onClick={() => remove(c.id)} className="btn-ghost h-8 w-8 p-0 text-brand-red" title="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-2">
        <input className="field" placeholder="Credential (e.g. TESDA NC II)" value={type} onChange={(e) => setType(e.target.value)} />
        <input className="field" placeholder="Number (optional)" value={number} onChange={(e) => setNumber(e.target.value)} />
        <input className="field" placeholder="Issuer (optional)" value={issuer} onChange={(e) => setIssuer(e.target.value)} />
      </div>
      {err && <p className="text-sm text-brand-red mt-2 flex items-center gap-1"><AlertCircle className="w-4 h-4" />{err}</p>}
      <button onClick={add} disabled={busy || type.trim().length < 2} className="btn-outline mt-3 disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}Add credential
      </button>
    </div>
  );
}
